import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Initial state
const initialState = {
  // Chat State
  isEnabled: false,
  isOpen: false,
  messages: [],
  isLoading: false,
  error: null,
  
  // Configuration
  config: {
    maxMessages: 100,
    autoScroll: true,
    showTimestamps: true,
    enableNotifications: true
  },
  
  // Status
  connectionStatus: 'disconnected', // disconnected, connecting, connected, error
  lastActivity: null
};

// Action types
const ChatActionTypes = {
  // Basic Chat Actions
  SET_ENABLED: 'SET_ENABLED',
  TOGGLE_CHAT: 'TOGGLE_CHAT',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  
  // Message Actions
  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  DELETE_MESSAGE: 'DELETE_MESSAGE',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  
  // Configuration Actions
  SET_CONFIG: 'SET_CONFIG',
  
  // Status Actions
  SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS',
  UPDATE_LAST_ACTIVITY: 'UPDATE_LAST_ACTIVITY'
};

// Reducer
const chatReducer = (state, action) => {
  switch (action.type) {
    case ChatActionTypes.SET_ENABLED:
      return {
        ...state,
        isEnabled: action.payload
      };
      
    case ChatActionTypes.TOGGLE_CHAT:
      return {
        ...state,
        isOpen: action.payload !== undefined ? action.payload : !state.isOpen
      };
      
    case ChatActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
      
    case ChatActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
      
    case ChatActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
      
    case ChatActionTypes.ADD_MESSAGE:
      const newMessages = [...state.messages, action.payload];
      // Limit messages to maxMessages
      const limitedMessages = newMessages.length > state.config.maxMessages 
        ? newMessages.slice(-state.config.maxMessages)
        : newMessages;
        
      return {
        ...state,
        messages: limitedMessages,
        lastActivity: new Date().toISOString()
      };
      
    case ChatActionTypes.UPDATE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map(msg => 
          msg.id === action.payload.id 
            ? { ...msg, ...action.payload.updates }
            : msg
        )
      };
      
    case ChatActionTypes.DELETE_MESSAGE:
      return {
        ...state,
        messages: state.messages.filter(msg => msg.id !== action.payload)
      };
      
    case ChatActionTypes.CLEAR_MESSAGES:
      return {
        ...state,
        messages: []
      };
      
    case ChatActionTypes.SET_CONFIG:
      return {
        ...state,
        config: {
          ...state.config,
          ...action.payload
        }
      };
      
    case ChatActionTypes.SET_CONNECTION_STATUS:
      return {
        ...state,
        connectionStatus: action.payload
      };
      
    case ChatActionTypes.UPDATE_LAST_ACTIVITY:
      return {
        ...state,
        lastActivity: new Date().toISOString()
      };
      
    default:
      return state;
  }
};

// Create context
const ChatContext = createContext();

// Context provider
export const ChatProvider = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  // Action creators
  const actions = {
    // Basic Actions
    setEnabled: useCallback((enabled) => {
      dispatch({ type: ChatActionTypes.SET_ENABLED, payload: enabled });
    }, []),
    
    toggleChat: useCallback((open) => {
      dispatch({ type: ChatActionTypes.TOGGLE_CHAT, payload: open });
    }, []),
    
    setLoading: useCallback((loading) => {
      dispatch({ type: ChatActionTypes.SET_LOADING, payload: loading });
    }, []),
    
    setError: useCallback((error) => {
      dispatch({ type: ChatActionTypes.SET_ERROR, payload: error });
    }, []),
    
    clearError: useCallback(() => {
      dispatch({ type: ChatActionTypes.CLEAR_ERROR });
    }, []),
    
    // Message Actions
    addMessage: useCallback((message) => {
      const messageWithId = {
        id: message.id || Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...message
      };
      dispatch({ type: ChatActionTypes.ADD_MESSAGE, payload: messageWithId });
    }, []),
    
    updateMessage: useCallback((id, updates) => {
      dispatch({ 
        type: ChatActionTypes.UPDATE_MESSAGE, 
        payload: { id, updates }
      });
    }, []),
    
    deleteMessage: useCallback((id) => {
      dispatch({ type: ChatActionTypes.DELETE_MESSAGE, payload: id });
    }, []),
    
    clearMessages: useCallback(() => {
      dispatch({ type: ChatActionTypes.CLEAR_MESSAGES });
    }, []),
    
    // Configuration Actions
    setConfig: useCallback((config) => {
      dispatch({ type: ChatActionTypes.SET_CONFIG, payload: config });
    }, []),
    
    // Status Actions
    setConnectionStatus: useCallback((status) => {
      dispatch({ type: ChatActionTypes.SET_CONNECTION_STATUS, payload: status });
    }, []),
    
    updateLastActivity: useCallback(() => {
      dispatch({ type: ChatActionTypes.UPDATE_LAST_ACTIVITY });
    }, [])
  };

  // Utility functions
  const utils = {
    // Export chat history
    exportChatHistory: useCallback(() => {
      const chatData = {
        timestamp: new Date().toISOString(),
        messages: state.messages,
        config: state.config
      };
      
      const blob = new Blob([JSON.stringify(chatData, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-history-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, [state.messages, state.config]),
    
    // Get message count
    getMessageCount: useCallback(() => state.messages.length, [state.messages]),
    
    // Get last message
    getLastMessage: useCallback(() => {
      return state.messages[state.messages.length - 1] || null;
    }, [state.messages]),
    
    // Check if chat has unread messages
    hasUnreadMessages: useCallback(() => {
      return state.messages.some(msg => msg.type === 'ai' && !msg.read);
    }, [state.messages])
  };

  const value = {
    state,
    actions,
    utils,
    // Computed values
    isReady: state.isEnabled && state.connectionStatus === 'connected',
    hasMessages: state.messages.length > 0,
    isConnected: state.connectionStatus === 'connected',
    hasError: !!state.error
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

// Custom hook to use the context
export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

// Export action types for external use
export { ChatActionTypes };